import StoreOrderStepBadge from '@/dealers/components/StoreOrderStepBadge'
import Badge from '@/gf/components/Badge'
import Filters from '@/gf/components/Filters'
import Layout from '@/gf/components/Layout'
import Link from '@/gf/components/Link'
import PaginationC from '@/gf/components/Pagination'
import PaymentStatusBadge from '@/gf/components/PaymentStatusBadge'
import Spinner from '@/gf/components/Spinner'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import useFilters from '@/gf/hooks/useFilters'
import Filter from '@/gf/modules/Filter'
import Money from '@/gf/modules/Money'
import StoreOrderM from '@/gf/modules/StoreOrder'
import Time from '@/gf/modules/Time'
import { Filter as FilterT, Pagination } from '../../types'
import fields from '../data/orderFilters/fields'
import { StoreOrdersSearch } from '../hooks/useSearchStoreOrdersNext'
import useSession from '../hooks/useSession'

const StoreOrdersTable = ({
  page,
  filters,
  updatePage,
  updateFilters,
  storeOrders,
  pagination,
  admin,
}: {
  page: number
  filters: FilterT[]
  updatePage: (newPage: number) => void
  updateFilters: (newFilters: FilterT[]) => void
  storeOrders?: StoreOrdersSearch['storeOrders']
  pagination?: Pagination
  admin: boolean
}) => {
  const { user, featureFlags } = useSession()
  const [stagedFilters, filtersApi] = useFilters(filters)

  const filterFields = Filter.availableFilterFields(fields, admin, featureFlags)

  const columns = 7 + (admin ? 2 : 0)

  const determineCustomerName = (storeOrder: StoreOrdersSearch['storeOrders'][number]) => {
    if (storeOrder.shippingAddress)
      return [storeOrder.shippingAddress.firstName, storeOrder.shippingAddress.lastName]
        .filter((n) => n)
        .join('  ')

    if (storeOrder.order.requestForQuote) return storeOrder.order.requestForQuote.fullName

    return storeOrder.order.name
  }

  return (
    <Layout>
      <Layout.Section type="table">
        <Filters
          filters={filters}
          stagedFilters={stagedFilters}
          fields={filterFields}
          filtersApi={filtersApi}
          onApply={() => updateFilters(stagedFilters)}
        />
        <Table>
          <Thead>
            <Tr>
              <Th>Order ID</Th>
              {admin && <Th>Parent Order ID</Th>}
              {admin && <Th>Store</Th>}
              <Th>Received</Th>
              <Th>Customer</Th>
              <Th>State</Th>
              <Th>Payment Status</Th>
              <Th>Updated</Th>
              <Th>Total</Th>
            </Tr>
          </Thead>
          <Tbody>
            {storeOrders &&
              storeOrders.map((storeOrder) => (
                <Tr key={storeOrder.id}>
                  <Td>
                    <Link.T to={`/orders/${storeOrder.id}`}>{storeOrder.shortId}</Link.T>
                  </Td>
                  {admin && <Td>{storeOrder.order.shortId}</Td>}
                  {admin && <Td>{storeOrder.store.name}</Td>}
                  <Td>{Time.toString(storeOrder.insertedAt)}</Td>
                  <Td>
                    {StoreOrderM.canDisplayCustomerContactInfo(storeOrder, user) &&
                      determineCustomerName(storeOrder)}
                  </Td>
                  <Td>
                    <StoreOrderStepBadge step={storeOrder.step} />
                    {!storeOrder.state && <Badge title="Empty" color="bg-gray-200" />}
                  </Td>
                  <Td>
                    <PaymentStatusBadge paymentStatus={storeOrder.paymentStatus} />
                  </Td>
                  <Td>{Time.toString(storeOrder.updatedAt)}</Td>
                  <Td>{Money.format(storeOrder.total)}</Td>
                </Tr>
              ))}

            {!storeOrders && (
              <Tr>
                <Td colSpan={columns}>
                  <Spinner />
                </Td>
              </Tr>
            )}

            {storeOrders && storeOrders.length === 0 && (
              <Tr>
                <Td colSpan={columns}>No orders found.</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Layout.Section>
      <Layout.Section type="full">
        <PaginationC pagination={pagination} page={page} updatePage={updatePage} />
      </Layout.Section>
    </Layout>
  )
}

export default StoreOrdersTable
